import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type PermissionResourceJSON = z.infer<typeof PermissionResourceSchema.ResourceJSONZ>;
export type PermissionResourceParams = z.infer<typeof PermissionResourceSchema.ResourceZ>;

export default class PermissionResourceSchema {
  public static readonly ResourceJSONZ = z.object({
    resourceId: z.uuid().nullish(),
    resourceType: z.enum(ResourceType),
    isDisabled: z.boolean().nullish(),
  });

  public static readonly ResourceZ = z.object({
    resourceId: z.instanceof(Guid).optional(),
    resourceType: z.enum(ResourceType),
    isDisabled: z.boolean().optional(),
  });

  public static validate(values: unknown): PermissionResourceParams {
    return parseOrThrow(PermissionResourceSchema.ResourceZ, values, {
      logError: true,
      schemaName: "PermissionResource",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): PermissionResourceJSON {
    return parseOrThrow(PermissionResourceSchema.ResourceJSONZ, values, {
      logError: true,
      schemaName: "PermissionResource",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is PermissionResourceParams {
    try {
      PermissionResourceSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is PermissionResourceJSON {
    try {
      PermissionResourceSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: PermissionResourceJSON): PermissionResourceParams {
    const parsed = parseOrThrow(PermissionResourceSchema.ResourceJSONZ, data, {
      logError: true,
      schemaName: "PermissionResource",
      operation: "deserialize",
      payloadSample: data,
    });
    return PermissionResourceSchema.codec.decode(parsed);
  }

  public static serialize(notification: PermissionResourceParams): PermissionResourceJSON {
    const parsed = PermissionResourceSchema.validate(notification);
    return PermissionResourceSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(PermissionResourceSchema.ResourceJSONZ, PermissionResourceSchema.ResourceZ, {
    decode: (data) => ({
      resourceId: Guid.fromJSON(data.resourceId) ?? undefined,
      resourceType: data.resourceType,
      isDisabled: data.isDisabled ?? undefined,
    }),
    encode: (resource) => ({
      resourceId: resource.resourceId?.toJSON(),
      resourceType: resource.resourceType,
      isDisabled: resource.isDisabled,
    }),
  });
}
